import { useEffect, useMemo, useRef, useState } from 'react';
import Alert from '@material-ui/lab/Alert';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import { useApi } from '@backstage/core-plugin-api';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import {
  createScaffolderFieldExtension,
  type FieldExtensionComponentProps,
  useTemplateSecrets,
} from '@backstage/plugin-scaffolder-react';
import { portalImageApiRef } from '../../apis/portalImageApi';
import type { ImageBuildSettings } from './portalImageTypes';
import { DEFAULT_IMAGE_BUILD_SETTINGS } from './portalImageTypes';
import styles from './imageBuildOverrides.module.css';

const GCP_REGIONS = [
  { value: 'us-east1', label: 'US East (South Carolina)' },
  { value: 'us-east4', label: 'US East (N. Virginia)' },
  { value: 'us-central1', label: 'US Central (Iowa)' },
  { value: 'us-west1', label: 'US West (Oregon)' },
  { value: 'europe-west1', label: 'EU (Belgium)' },
  { value: 'europe-west4', label: 'EU (Netherlands)' },
] as const;

type StepOneData = {
  source_repo_url?: string;
  source_repo_ref?: string;
};

function toSettings(resolved: ImageBuildSettings): ImageBuildSettings {
  return {
    image_version: resolved.image_version,
    gcp_project_id: resolved.gcp_project_id,
    gcp_region: resolved.gcp_region,
    gcp_zone: resolved.gcp_zone,
    disk_size: Number(resolved.disk_size),
  };
}

function sanitizeCloneToken(token?: string): string | undefined {
  const trimmed = token?.trim();
  if (!trimmed || /^\*+$/.test(trimmed)) {
    return undefined;
  }
  return trimmed;
}

function mergeSettings(
  current?: Partial<ImageBuildSettings>,
): ImageBuildSettings {
  return { ...DEFAULT_IMAGE_BUILD_SETTINGS, ...(current ?? {}) };
}

const ImageBuildOverrides = (
  props: FieldExtensionComponentProps<ImageBuildSettings | undefined>,
) => {
  const { onChange, formData, rawErrors, formContext } = props;
  const portalImageApi = useApi(portalImageApiRef);
  const { secrets } = useTemplateSecrets();
  const cloneToken = sanitizeCloneToken(secrets.clone_token);

  const stepOne = (formContext?.formData ?? {}) as StepOneData;
  const sourceRepoUrl = stepOne.source_repo_url?.trim() ?? '';
  const sourceRepoRef = stepOne.source_repo_ref?.trim() || 'main';
  const hasPat = Boolean(cloneToken);
  const fetchKey = `${sourceRepoUrl}@${sourceRepoRef}@${hasPat ? 'pat' : 'nopat'}`;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [manifestFound, setManifestFound] = useState<boolean | null>(null);
  const [repoAccessible, setRepoAccessible] = useState<boolean | null>(null);
  const [loadedSettings, setLoadedSettings] = useState<ImageBuildSettings | null>(
    null,
  );

  // Keep latest onChange without re-triggering the fetch effect (RJSF often
  // recreates onChange every render, which previously re-applied defaults and
  // wiped what the user was typing).
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const fetchedKeyRef = useRef<string | null>(null);
  const userEditedRef = useRef(false);

  const settings = useMemo<ImageBuildSettings>(
    () => loadedSettings ?? mergeSettings(formData),
    [formData, loadedSettings],
  );

  useEffect(() => {
    if (!sourceRepoUrl) {
      return undefined;
    }

    // One resolve per repo/ref/PAT — do not refetch when the user edits fields.
    if (fetchedKeyRef.current === fetchKey) {
      return undefined;
    }

    let cancelled = false;
    userEditedRef.current = false;
    setLoading(true);
    setError(null);

    portalImageApi
      .resolveBuild({
        source_repo_url: sourceRepoUrl,
        source_repo_ref: sourceRepoRef,
        clone_token: cloneToken,
      })
      .then(resolved => {
        if (cancelled) {
          return;
        }
        fetchedKeyRef.current = fetchKey;
        setManifestFound(resolved.manifestFound);
        setRepoAccessible(resolved.repoAccessible);

        // If the user already typed while the request was in flight, keep their
        // values — only update the status banner.
        if (userEditedRef.current) {
          return;
        }

        const next = toSettings(resolved);
        setLoadedSettings(next);
        onChangeRef.current(next);
      })
      .catch(err => {
        if (cancelled) {
          return;
        }
        fetchedKeyRef.current = fetchKey;
        setManifestFound(null);
        setRepoAccessible(null);
        setError(
          err instanceof Error ? err.message : 'Could not load image-values.yaml',
        );
        if (!userEditedRef.current && !formData) {
          setLoadedSettings(DEFAULT_IMAGE_BUILD_SETTINGS);
          onChangeRef.current(DEFAULT_IMAGE_BUILD_SETTINGS);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // Intentionally omit onChange/formData — see onChangeRef / userEditedRef.
  }, [fetchKey, portalImageApi, cloneToken, sourceRepoRef, sourceRepoUrl]);

  const update = (patch: Partial<ImageBuildSettings>) => {
    userEditedRef.current = true;
    const next = { ...settings, ...patch };
    setLoadedSettings(next);
    onChange(next);
  };

  const statusMessage = (() => {
    if (loading) {
      return 'Loading image-values.yaml from your repo…';
    }
    if (error) {
      return error;
    }
    if (repoAccessible === false) {
      return hasPat
        ? 'Cannot read this repository with the current token — check PAT scope (repo) and branch.'
        : 'Private repository: add a Repository Access Token on the previous step, then return here.';
    }
    if (manifestFound) {
      return 'Loaded from image-values.yaml — edit any field below.';
    }
    if (manifestFound === false && repoAccessible) {
      return `No image-values.yaml at repo root on branch "${sourceRepoRef}". Showing PS defaults — edit freely; your changes are kept.`;
    }
    return null;
  })();

  return (
    <div className={styles.imageBuildOverrides}>
      {statusMessage && (
        <Alert
          className={styles.statusBanner}
          severity={
            loading
              ? 'info'
              : error || repoAccessible === false
                ? 'warning'
                : manifestFound
                  ? 'success'
                  : 'info'
          }
          icon={loading ? <CircularProgress size={18} /> : undefined}
        >
          {statusMessage}
        </Alert>
      )}

      <div className={styles.fieldGrid}>
        <TextField
          label="Image Version"
          value={settings.image_version}
          onChange={e => update({ image_version: e.target.value })}
          helperText="Example: 0.8.1 or v1.0.2"
          fullWidth
          error={!!rawErrors?.length}
        />

        <TextField
          label="GCP Project ID"
          value={settings.gcp_project_id}
          onChange={e => update({ gcp_project_id: e.target.value })}
          fullWidth
        />

        <FormControl fullWidth>
          <InputLabel id="gcp-region-label">GCP Region</InputLabel>
          <Select
            labelId="gcp-region-label"
            value={settings.gcp_region}
            onChange={e => update({ gcp_region: e.target.value as string })}
          >
            {GCP_REGIONS.map(region => (
              <MenuItem key={region.value} value={region.value}>
                {region.label}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>Region for the Packer build VM</FormHelperText>
        </FormControl>

        <TextField
          label="GCP Zone"
          value={settings.gcp_zone}
          onChange={e => update({ gcp_zone: e.target.value })}
          helperText="Example: us-east1-b"
          fullWidth
        />

        <TextField
          label="Disk Size (GB)"
          type="number"
          value={settings.disk_size}
          onChange={e => update({ disk_size: Number(e.target.value) })}
          inputProps={{ min: 50, max: 500 }}
          fullWidth
        />
      </div>
    </div>
  );
};

export const ImageBuildOverridesFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'ImageBuildOverrides',
    component: ImageBuildOverrides,
  }),
);
